// =========================
// lib/support.js  (BL-037 Story 37.1 — Feedback & Support Capture)
//
// Authenticated client for the customer feedback/support channel. Unlike
// lib/waitlist (anonymous landing-page capture), these calls send the current
// Supabase session JWT as a Bearer token — the submitter is a signed-in user
// and the backing support_requests table is RLS-protected + user-owned.
//
// Return-shape contract (mirrors lib/admin / lib/waitlist):
//   success → { data, error: null }
//   failure → { data: null, error: { message } }   (never throws on the happy
//             paths; fail-closed with NO network call when there is no session)
//
// URL convention matches every other Netlify-function caller:
// ${API_BASE_URL}/<function-name> (api/* → /.netlify/functions/* via netlify.toml).
// =========================

import { supabase } from "./supabaseClient";
import { API_BASE_URL } from "../services/api";

async function getAccessToken() {
  const { data } = await supabase.auth.getSession();
  return data?.session?.access_token || null;
}

// Create a support request (feedback | support | bug | feature_request).
export async function submitSupportRequest({ category, subject, message } = {}) {
  const token = await getAccessToken();
  if (!token) return { data: null, error: { message: "Not signed in" } };
  try {
    const response = await fetch(`${API_BASE_URL}/submit-support-request`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ category, subject, message }),
    });
    const body = await response.json().catch(() => null);
    if (!response.ok || body?.success === false) {
      return {
        data: null,
        error: { message: body?.error || `HTTP ${response.status}` },
      };
    }
    return { data: body, error: null };
  } catch (err) {
    return { data: null, error: { message: err?.message || "Network error." } };
  }
}

// List the signed-in user's own support requests (newest first). Data comes
// from body.data, mirroring lib/admin's list helpers.
export async function listMySupportRequests() {
  const token = await getAccessToken();
  if (!token) return { data: null, error: { message: "Not signed in" } };
  try {
    const response = await fetch(`${API_BASE_URL}/list-support-requests`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    const body = await response.json().catch(() => null);
    if (!response.ok || body?.success === false) {
      return {
        data: null,
        error: { message: body?.error || `HTTP ${response.status}` },
      };
    }
    return { data: body.data, error: null };
  } catch (err) {
    return { data: null, error: { message: err?.message || "Network error." } };
  }
}
